import { fetchArPhotos, fetchMapPhotos, fetchSpherePhotos, fetchPosters, createEventsStream } from '../../services/apiClient.js';
import { initSiteHeader } from '../ui/siteHeader.js';

initSiteHeader();

// ── Données : photos AR, carte, sphère (Sillages) et posters confondus, plus récent d'abord ──
const gridEl = document.getElementById('escalesGrid');
const emptyEl = document.getElementById('escalesEmpty');
const emptyMsgEl = document.getElementById('escalesEmptyMsg');

// Pop-up seulement avec une vraie souris sur un écran large (demande Rudy :
// "le pop up uniquement en desktop") ; ailleurs, un tap affiche les infos.
const desktopQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 601px)');

// Nombre de miniatures chargées tout de suite (1re rangée et un peu plus) ;
// les suivantes attendent d'approcher de l'écran (loading="lazy").
const EAGER_COUNT = 6;

let entries = [];
const itemByKey = new Map();

// Lien du bandeau : la carte pour les photos AR/carte géolocalisées, la
// page Sillages (même cadrage qu'à la prise) pour les photos sphère.
function photoLink(photo, kind) {
  if (kind === 'sphere') return { href: `/sillages?photo=sphere-${photo.id}`, label: 'Voir Sillages' };
  const hasLocation = typeof photo.lat === 'number' && typeof photo.lng === 'number';
  return hasLocation ? { href: `/carte?photo=${kind}-${photo.id}`, label: 'Voir sur la carte' } : null;
}

function toEntry(photo, kind) {
  return {
    key: `${kind}-${photo.id}`,
    id: photo.id,
    kind,
    pseudo: (photo.pseudo || '').trim(),
    time: new Date(photo.createdAt).getTime(),
    imageBase: `/api/${kind}-photos/${photo.id}/image`,
    link: photoLink(photo, kind),
  };
}

// Poster : le créateur à gauche ; à droite, téléchargement du fichier HD
// (PNG pour l'impression, JPEG léger) et "Commander" (shop).
function toPosterEntry(poster) {
  return {
    key: `poster-${poster.id}`,
    id: poster.id,
    kind: 'poster',
    pseudo: (poster.pseudo || '').trim(),
    time: new Date(poster.createdAt).getTime(),
    imageBase: `/api/posters/${poster.id}/image`,
    link: null,
    downloads: poster.downloads,
  };
}

function infoLink(href, text, download = false) {
  const anchor = document.createElement('a');
  anchor.className = 'escales-info-map';
  anchor.href = href;
  anchor.textContent = text;
  if (download) anchor.setAttribute('download', '');
  return anchor;
}

function posterActions(entry) {
  const actions = document.createElement('div');
  actions.className = 'escales-info-actions';
  const download = document.createElement('div');
  download.className = 'escales-info-download';
  const label = document.createElement('span');
  label.textContent = 'Télécharger';
  download.append(label, infoLink(entry.downloads.png, 'PNG', true), infoLink(entry.downloads.jpg, 'JPEG', true));
  actions.append(download, infoLink(`/shop?poster=${entry.id}`, 'Commander'));
  return actions;
}

function altText(entry) {
  if (entry.kind === 'poster') return `Poster créé par ${entry.pseudo || 'Anonyme'}`;
  if (entry.kind === 'sphere') return entry.pseudo ? `Photo de la page Sillages : ${entry.pseudo}` : 'Photo de la page Sillages';
  return entry.pseudo ? `Photo de ${entry.pseudo}` : 'Photo';
}

function formatDate(time) {
  return new Date(time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Bandeau noir du bas : pseudo + date à gauche, "Voir sur la carte" (ou
// "Voir Sillages") à droite.
function fillInfo(infoEl, entry) {
  infoEl.replaceChildren();
  const textEl = document.createElement('div');
  textEl.className = 'escales-info-text';
  // Poster non signé : "Anonyme".
  if (entry.pseudo || entry.kind === 'poster') {
    const pseudoEl = document.createElement('span');
    pseudoEl.className = 'escales-info-pseudo';
    // Photo sphère : liste de pseudos possible, affichée en entier.
    if (entry.kind === 'sphere') pseudoEl.classList.add('escales-info-pseudo--list');
    pseudoEl.textContent = entry.kind === 'poster' ? `Poster de ${entry.pseudo || 'Anonyme'}` : entry.pseudo;
    textEl.appendChild(pseudoEl);
  }
  const dateEl = document.createElement('span');
  dateEl.className = 'escales-info-date';
  dateEl.textContent = formatDate(entry.time);
  textEl.appendChild(dateEl);
  infoEl.appendChild(textEl);
  if (entry.kind === 'poster') infoEl.appendChild(posterActions(entry));
  if (entry.link) {
    const mapLink = document.createElement('a');
    mapLink.className = 'escales-info-map';
    mapLink.href = entry.link.href;
    mapLink.textContent = entry.link.label;
    infoEl.appendChild(mapLink);
  }
}

function createItem(entry, index) {
  const li = document.createElement('li');
  li.className = 'escales-item';
  li.dataset.key = entry.key;

  const img = document.createElement('img');
  img.className = 'escales-img';
  img.alt = altText(entry);
  img.decoding = 'async';
  img.loading = index < EAGER_COUNT ? 'eager' : 'lazy';
  img.addEventListener('load', () => li.classList.add('is-loaded'), { once: true });
  img.src = `${entry.imageBase}?size=thumb`;

  const info = document.createElement('div');
  info.className = 'escales-info';

  li.append(img, info);
  return { li, info, entry };
}

function render() {
  if (!emptyEl.classList.contains('is-loading')) emptyEl.hidden = entries.length > 0;

  const liveKeys = new Set(entries.map((e) => e.key));
  for (const [key, item] of itemByKey) {
    if (!liveKeys.has(key)) {
      item.li.remove();
      itemByKey.delete(key);
    }
  }

  // appendChild déplace les éléments déjà présents sans recharger leur image.
  const fragment = document.createDocumentFragment();
  entries.forEach((entry, index) => {
    let item = itemByKey.get(entry.key);
    if (!item) {
      item = createItem(entry, index);
      itemByKey.set(entry.key, item);
    }
    item.entry = entry;
    fillInfo(item.info, entry);
    fragment.appendChild(item.li);
  });
  gridEl.appendChild(fragment);
}

async function load() {
  const [arPhotos, mapPhotos, spherePhotos, posters] = await Promise.all([
    fetchArPhotos(),
    fetchMapPhotos(),
    // Une panne de ces flux ne doit pas vider toute la page.
    fetchSpherePhotos().catch((error) => {
      console.error('[escales] photos sphère', error);
      return [];
    }),
    fetchPosters().catch((error) => {
      console.error('[escales] posters', error);
      return [];
    }),
  ]);
  entries = [
    ...arPhotos.map((p) => toEntry(p, 'ar')),
    ...mapPhotos.map((p) => toEntry(p, 'map')),
    ...spherePhotos.map((p) => toEntry(p, 'sphere')),
    ...posters.map(toPosterEntry),
  ].sort((a, b) => (b.time - a.time) || (b.id - a.id));
  render();
  if (lightboxKey) refreshLightbox();
}

// Loader (même que la galerie) : retiré une fois les premières miniatures
// affichées -- pas seulement la liste reçue, sinon on verrait des cases
// grises vides. Plafonné pour ne jamais rester bloqué sur une image lente.
const LOADER_MAX_WAIT_MS = 4000;

function waitForFirstImages() {
  const imgs = [...gridEl.querySelectorAll('.escales-img')].slice(0, EAGER_COUNT);
  const loaded = Promise.all(imgs.map((img) => (img.complete ? null : new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
  }))));
  return Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, LOADER_MAX_WAIT_MS))]);
}

function hideLoader() {
  emptyEl.classList.remove('is-loading');
  emptyEl.hidden = entries.length > 0;
}

// ── Clic / tap sur une photo ─────────────────────────────────────────────
function closeAllInfos(exceptLi = null) {
  gridEl.querySelectorAll('.escales-item.is-info-open').forEach((li) => {
    if (li !== exceptLi) li.classList.remove('is-info-open');
  });
}

gridEl.addEventListener('click', (event) => {
  if (event.target.closest('.escales-info-map')) return; // lien : navigation normale
  const li = event.target.closest('.escales-item');
  if (!li) return;
  if (desktopQuery.matches) {
    openLightbox(li.dataset.key);
  } else {
    closeAllInfos(li);
    li.classList.toggle('is-info-open');
  }
});

// Tap en dehors d'une photo : referme le bandeau ouvert (mobile).
document.addEventListener('click', (event) => {
  if (!event.target.closest('.escales-item')) closeAllInfos();
});

// ── Pop-up (desktop) ─────────────────────────────────────────────────────
const lightboxEl = document.getElementById('escalesLightbox');
const lightboxImg = document.getElementById('escalesLightboxImg');
const lightboxInfo = document.getElementById('escalesLightboxInfo');
const lightboxCount = document.getElementById('escalesLightboxCount');
let lightboxKey = null;

function lightboxIndex() {
  return entries.findIndex((e) => e.key === lightboxKey);
}

function preload(entry) {
  if (entry) new Image().src = `${entry.imageBase}?size=web`;
}

function refreshLightbox() {
  const index = lightboxIndex();
  if (index === -1) { closeLightbox(); return; } // photo supprimée entre-temps
  const entry = entries[index];
  const src = `${entry.imageBase}?size=web`;
  if (lightboxImg.getAttribute('src') !== src) lightboxImg.src = src;
  lightboxImg.alt = altText(entry);
  fillInfo(lightboxInfo, entry);
  // Numérotée par ordre d'arrivée : la plus ancienne = 1, la plus récente = total.
  lightboxCount.textContent = `${entries.length - index} / ${entries.length}`;
  const n = entries.length;
  preload(entries[(index + 1) % n]);
  preload(entries[(index - 1 + n) % n]);
}

function openLightbox(key) {
  lightboxKey = key;
  refreshLightbox();
  lightboxEl.hidden = false;
  document.body.classList.add('escales-lightbox-open');
}

function closeLightbox() {
  lightboxKey = null;
  lightboxEl.hidden = true;
  lightboxImg.removeAttribute('src');
  document.body.classList.remove('escales-lightbox-open');
}

// Navigation en boucle : après la dernière (la plus ancienne), la première.
function stepLightbox(delta) {
  const n = entries.length;
  const index = lightboxIndex();
  if (index === -1 || n === 0) return;
  lightboxKey = entries[(index + delta + n) % n].key;
  refreshLightbox();
}

document.getElementById('escalesLightboxClose').addEventListener('click', closeLightbox);
document.getElementById('escalesLightboxPrev').addEventListener('click', () => stepLightbox(-1));
document.getElementById('escalesLightboxNext').addEventListener('click', () => stepLightbox(1));

// Clic sur le fond (pas sur la photo ni sur un bouton) : ferme.
lightboxEl.addEventListener('click', (event) => {
  if (event.target === lightboxEl) closeLightbox();
});

document.addEventListener('keydown', (event) => {
  if (!lightboxKey) return;
  if (event.key === 'Escape') closeLightbox();
  else if (event.key === 'ArrowLeft') stepLightbox(-1);
  else if (event.key === 'ArrowRight') stepLightbox(1);
});

// ── Démarrage + mise à jour en direct (même flux que Sillage) ────────────
load()
  .then(waitForFirstImages)
  .then(hideLoader)
  .catch((error) => {
    console.error('[escales] chargement des photos', error);
    emptyMsgEl.textContent = 'Impossible de charger les photos.';
    hideLoader();
    emptyEl.hidden = false;
  });

let refreshTimer = null;
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    load().catch((error) => console.error('[escales] mise à jour en direct', error));
  }, 300);
}

const liveEvents = createEventsStream();
[
  'new-ar-photo', 'update-ar-photo', 'delete-ar-photo',
  'new-map-photo', 'update-map-photo', 'delete-map-photo',
  'new-sphere-photo', 'update-sphere-photo', 'delete-sphere-photo',
  'new-poster', 'update-poster', 'delete-poster',
].forEach((eventName) => liveEvents.addEventListener(eventName, scheduleRefresh));
